// Wysyłka: XHR (upload.onprogress daje realny pasek postępu przy plikach),
// timeout 60 s, retry, awaryjne „wyślij bez plików", mapowanie błędów 4xx
// na kroki, sukces = podmiana widoku + magic-link z odpowiedzi.

import { announce, focusHeading } from "./a11y.js";
import { setFieldError, validateStep } from "./validate.js";
import { clearDraft } from "./draft.js";
import { HONEYPOT } from "./config.js";

export function initSubmit(form, api) {
    const submitBtn = form.querySelector("[data-submit]");
    const formError = form.querySelector("[data-form-error]");
    const endpoint = form.dataset.endpoint;
    const ticketEndpoint = form.dataset.ticketEndpoint;

    let ticket = "";
    let busy = false;

    // Bilet HMAC dociągany z /wp-json (poza page cache) od razu na starcie —
    // serwer i tak wymaga, żeby miał co najmniej 8 sekund.
    function fetchTicket() {
        if (!ticketEndpoint) return Promise.resolve("");
        return fetch(ticketEndpoint, { cache: "no-store" })
            .then((res) => res.json())
            .then((data) => {
                ticket = data && data.ticket ? data.ticket : "";
                return ticket;
            })
            .catch(() => "");
    }
    fetchTicket();

    // Etykieta przycisku zostaje; zmienia się tylko, gdy niesie realny postęp
    // wysyłki plików. Blokada przycisku dopiero po starcie wysyłki — chroni
    // przed podwójnym submitem, nie przed próbą wysłania niekompletnego formularza.
    function setBusy(on, label) {
        busy = on;
        if (!submitBtn) return;
        const text = submitBtn.querySelector("[data-submit-label]");
        submitBtn.disabled = on;
        submitBtn.setAttribute("aria-busy", on ? "true" : "false");
        if (text) text.textContent = on ? label || "Wysyłanie…" : "Wyślij brief";
    }

    function showFormError(message, extraButtons = []) {
        if (!formError) return;
        formError.innerHTML = "";
        const p = document.createElement("p");
        p.textContent = message;
        formError.appendChild(p);
        const actions = document.createElement("p");
        actions.className = "brief__formErrorActions";
        const retry = document.createElement("button");
        retry.type = "button";
        retry.className = "mainBtn";
        retry.textContent = "Spróbuj ponownie";
        retry.addEventListener("click", () => doSubmit(false));
        actions.appendChild(retry);
        extraButtons.forEach((btn) => actions.appendChild(btn));
        formError.appendChild(actions);
        formError.hidden = false;
        focusHeading(formError);
    }

    function clearFormError() {
        if (formError) {
            formError.hidden = true;
            formError.innerHTML = "";
        }
    }

    function buildFormData(withFiles) {
        const data = new FormData(form); // disabled wypadają same
        // honeypot: autofill przeglądarki potrafi wypełnić ukryte pole —
        // w przepływie z JS zerujemy je zawsze (boty bez JS go nie wyzerują)
        data.set(HONEYPOT, "");
        data.append("ticket", ticket);
        if (withFiles && api.uploads) {
            const files = api.uploads.collect();
            Object.entries(files).forEach(([name, list]) => {
                list.forEach((file) => data.append(name, file, file.name));
            });
        }
        return data;
    }

    function send(data, onProgress) {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", endpoint);
            xhr.timeout = 60000;
            xhr.responseType = "json";
            if (xhr.upload && onProgress) {
                xhr.upload.addEventListener("progress", (event) => {
                    if (event.lengthComputable) onProgress(event.loaded / event.total);
                });
            }
            xhr.addEventListener("load", () =>
                resolve({ status: xhr.status, body: xhr.response })
            );
            xhr.addEventListener("error", () => reject(new Error("network")));
            xhr.addEventListener("timeout", () => reject(new Error("timeout")));
            xhr.send(data);
        });
    }

    function applyFieldErrors(fields) {
        let firstStep = 0;
        Object.entries(fields || {}).forEach(([name, message]) => {
            const input = form.querySelector(
                '[name="' + CSS.escape(name) + '"], [name="' + CSS.escape(name) + '[]"]'
            );
            if (!input) return;
            setFieldError(input, message);
            const stepSection = input.closest(".brief__step");
            const stepNr = stepSection ? Number(stepSection.dataset.step) : 0;
            if (stepNr && (!firstStep || stepNr < firstStep)) firstStep = stepNr;
        });
        if (firstStep && api.steps) api.steps.goTo(firstStep);
    }

    function showSuccess(materialsUrl, id) {
        clearDraft();
        form.hidden = true;
        const intro = document.querySelector("[data-intro]");
        if (intro) intro.hidden = true;
        const tasklist = document.querySelector("[data-tasklist]");
        if (tasklist) tasklist.hidden = true;

        // Szyna kroków znika, ale układ musi przejść na jedną kolumnę: samo
        // hidden wyrzuca <main> z siatki do wąskiej kolumny po lewej.
        const layout = document.querySelector(".briefLayout");
        if (layout) layout.classList.add("briefLayout--single");
        const rail = document.querySelector(".briefRail");
        if (rail) rail.hidden = true;

        const success = document.querySelector("[data-success]");
        if (success) {
            success.hidden = false;
            const link = success.querySelector("[data-materials-link]");
            if (link && materialsUrl) {
                link.href = materialsUrl;
                link.hidden = false;
            }
            const ref = success.querySelector("[data-success-ref]");
            const refId = success.querySelector("[data-success-id]");
            if (ref && refId && id) {
                refId.textContent = "#" + id;
                ref.hidden = false;
            }
            const title = success.querySelector(".brief__successTitle");
            focusHeading(title);
        }
        announce("Zgłoszenie wysłane. Dziękujemy!");
        if (window.dataLayer) {
            window.dataLayer.push({ event: "brief_submitted" });
        }
    }

    function doSubmit(withFiles) {
        if (busy) return;
        clearFormError();

        const badStep = api.steps ? api.steps.validateAll() : 0;
        if (badStep) {
            api.steps.goTo(badStep);
            const el = api.steps.stepEl(badStep);
            if (el) {
                validateStep(el, form); // ponownie, z fokusem na podsumowaniu
            }
            return;
        }

        const run = () => {
            setBusy(true);
            const hasFiles = withFiles && api.uploads && api.uploads.hasFiles();
            send(buildFormData(withFiles), (ratio) => {
                if (hasFiles) {
                    setBusy(true, "Wysyłanie… " + Math.round(ratio * 100) + "%");
                }
            })
                .then(({ status, body }) => {
                    setBusy(false);
                    if (status === 201 && body && body.id !== undefined) {
                        showSuccess(body.materialy_url || "", body.id);
                        return;
                    }
                    if (status === 400 && body && body.fields) {
                        applyFieldErrors(body.fields);
                        return;
                    }
                    if (status === 403) {
                        // bilet wygasł/nieświeży — dociągamy nowy i prosimy o ponowienie
                        fetchTicket().then(() =>
                            showFormError(
                                body && body.message
                                    ? body.message
                                    : "Sesja wygasła — kliknij „Spróbuj ponownie”."
                            )
                        );
                        return;
                    }
                    if (status === 429) {
                        showFormError(
                            body && body.message ? body.message : "Za dużo prób. Spróbuj za godzinę."
                        );
                        return;
                    }
                    showFormError("Coś poszło nie tak po naszej stronie. Spróbuj ponownie za chwilę.");
                })
                .catch(() => {
                    setBusy(false);
                    const extra = [];
                    if (withFiles && api.uploads && api.uploads.hasFiles()) {
                        const skipFiles = document.createElement("button");
                        skipFiles.type = "button";
                        skipFiles.className = "brief__linkBtn";
                        skipFiles.textContent = "Wyślij bez plików — doślesz je później";
                        skipFiles.addEventListener("click", () => doSubmit(false));
                        extra.push(skipFiles);
                    }
                    showFormError(
                        "Nie udało się wysłać. Sprawdź połączenie z internetem i spróbuj ponownie.",
                        extra
                    );
                });
        };

        if (!ticket) {
            fetchTicket().then(run);
        } else {
            run();
        }
    }

    form.addEventListener("submit", (event) => {
        event.preventDefault();
        doSubmit(true);
    });

    return { doSubmit };
}
